import { InsuredPerson } from '@app/domain/model/eshop/personal-accidents/InsuredPerson';
import { generateId } from '@app/lib/id-generator/generateId';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { useCallback, useState } from 'react';

function ensurePersonHasId(person: InsuredPerson): InsuredPerson {
    return {
        ...person,
        id: person.id || generateId(),
    };
}

function getInitialInsuredPersons(initialPersons?: InsuredPerson[]): InsuredPerson[] {
    if (initialPersons && initialPersons.length > 0) {
        return initialPersons.map(ensurePersonHasId);
    }

    return [
        {
            id: generateId(),
            age: 0,
            isPolicyholder: false,
            name: '',
            surname: '',
            personalCode: '',
        },
    ];
}

export function useInsuredPersonsState(initialInsuredPersons?: InsuredPerson[]) {
    const [insuredPersons, setInsuredPersons] = useState<InsuredPerson[]>(
        getInitialInsuredPersons(initialInsuredPersons),
    );

    const handleAddPerson = useCallback(() => {
        if (insuredPersons.length < 5) {
            setInsuredPersons([
                ...insuredPersons,
                {
                    id: generateId(),
                    age: 0,
                    isPolicyholder: false,
                    name: '',
                    surname: '',
                    personalCode: '',
                },
            ]);
        }
    }, [insuredPersons]);

    const handleRemovePerson = useCallback(
        (id: string) => {
            if (insuredPersons.length > 1) {
                setInsuredPersons(insuredPersons.filter(person => person.id !== id));
            }
        },
        [insuredPersons],
    );

    const handleAgeChange = (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const age = parseInt(e.target.value, 0) || 0;
        setInsuredPersons(insuredPersons.map(person => (person.id === id ? { ...person, age } : person)));
    };

    const handleNameChange = (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setInsuredPersons(insuredPersons.map(person => (person.id === id ? { ...person, name } : person)));
    };

    const handleSurnameChange = (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const surname = e.target.value;
        setInsuredPersons(insuredPersons.map(person => (person.id === id ? { ...person, surname } : person)));
    };

    const handlePersonalCodeChange = (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const personalCode = e.target.value;
        setInsuredPersons(insuredPersons.map(person => (person.id === id ? { ...person, personalCode } : person)));
    };

    const updatePrimaryPolicyholderData = useCallback((name: string, surname: string, personalCode: string) => {
        setInsuredPersons(persons => {
            const updatedPersons = [...persons];

            const parsedData = parseLithuanianPersonalCode(personalCode);
            const policyholderAge = parsedData.isValid ? parsedData.age : undefined;

            let targetIndex = -1;

            targetIndex = updatedPersons.findIndex(person => person.isPolicyholder);

            if (targetIndex === -1 && policyholderAge) {
                targetIndex = updatedPersons.findIndex(
                    person => !person.personalCode && person.age === policyholderAge,
                );
            }

            if (targetIndex === -1) {
                targetIndex = 0;
            }

            updatedPersons[targetIndex] = {
                ...updatedPersons[targetIndex],
                name,
                surname,
                personalCode,
                isPolicyholder: true,
            };
            return updatedPersons;
        });
    }, []);

    const revertPrimaryPolicyholderData = useCallback(() => {
        setInsuredPersons(persons => {
            const primaryPersonIndex = persons.findIndex(person => person.isPolicyholder);
            if (primaryPersonIndex !== -1) {
                const updatedPersons = [...persons];
                updatedPersons[primaryPersonIndex] = {
                    ...updatedPersons[primaryPersonIndex],
                    name: '',
                    surname: '',
                    personalCode: '',
                    isPolicyholder: false,
                };
                return updatedPersons;
            }
            return persons;
        });
    }, []);

    return {
        insuredPersons,
        setInsuredPersons,
        handleAddPerson,
        handleRemovePerson,
        handleAgeChange,
        handleNameChange,
        handleSurnameChange,
        handlePersonalCodeChange,
        updatePrimaryPolicyholderData,
        revertPrimaryPolicyholderData,
    };
}
